// Angular
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
// Material
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
// RxJS
import { BehaviorSubject, Subscription } from 'rxjs';
// NGRX
import { select, Store } from '@ngrx/store';
import { AppState } from '../../../../../core/reducers';
// Services and Models
import {
  ContactActionTypes,
  ContactModel,
  ContactOnServerCreated,
  ContactsService,
  selectContactErrorState,
  selectContactLatestSuccessfullAction,
  selectContactsActionLoading,
  SharedHttpRequestService,
} from '../../../../../core/lists';
import { currentUser } from 'src/app/core/auth';
import { TranslateService } from '@ngx-translate/core';
import { skip } from 'rxjs/operators';
import { DatePipe } from '@angular/common';
import { UserService } from '../../../../../core/auth/_services';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';
import { LanguagePreference } from '../../../../../core/lists/_models/languagePreference.model';
import { tagsViewContext } from '../../../../../core/lists/_services/shared-http-request.service';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'kt-contact-add',
  templateUrl: './contact-add.dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateContactDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  contactForm: FormGroup;
  hasFormErrors: boolean;
  clicked: boolean;
  companyId: number;
  private subscriptions: Subscription[] = [];
  @Input() idx;
  @Input() fromHis = false;
  history = [];
  contactHistory = [];
  tags = new FormControl();
  tags$ = new BehaviorSubject<any>([]);
  loadingTags = true;
  readOnlyTags = [];
  maxDate: any;
  languages: LanguagePreference[];
  urbanizationValue;
  Address1Value;
  Address2Value;
  cityValue;
  zipValue;
  stats;
  languageValue;
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  basicInfo: any = {};
  prevData;
  nextData;
  tagschanged = false;
  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private datePipe: DatePipe,
    public dialogRef: MatDialogRef<CreateContactDialogComponent>,
    private store: Store<AppState>,
    private contactFB: FormBuilder,
    private translate: TranslateService,
    public dialog: MatDialog,
    public contactService: ContactsService,
    public userService: UserService,
    private cdr: ChangeDetectorRef,
    private sharedHttpRequestService: SharedHttpRequestService,
  ) {
    this.maxDate = new Date();
  }

  onAlertClose() {
    this.hasFormErrors = false;
  }

  setMaxDate($event: MatDatepickerInputEvent<any>) {
    this.maxDate = $event.value;
  }

  ngAfterViewInit() {
    if (this.data) {
      this.languageValue = this.data.contact?.language?.id;
      this.cdr.detectChanges();
    }
  }

  private loadTags() {
    if (this.data.contact)
      this.sharedHttpRequestService
        .getTagsForView(this.data.contact.id, tagsViewContext.CONTACTS)
        .subscribe(
          (tags) => {
            this.readOnlyTags = tags.data;
            this.tags$.next(tags.data);
            this.loadingTags = false;
          },
          () => {
            this.readOnlyTags = [];
            this.loadingTags = false;
          },
        );
  }

  showChanges(dataBefore, dataAfter) {
    return dataBefore !== dataAfter;
  }
  compareTags(dataBefore, dataAfter) {
    return JSON.stringify(dataAfter) !== JSON.stringify(dataBefore);
  }
  ngOnInit() {
    if (Array.isArray(this.data) && this.data.length > 0) {
      if (this.data.length > 1) {
        this.prevData = this.data[0].contact;
        this.nextData = this.data[1].contact;
      } else if (this.data.length === 1) {
        this.prevData = this.nextData = this.data[this.idx].contact;
      }
      this.data = this.data[this.idx];
      this.contactService.avatarPresigned(this.data.contact.avatar).subscribe((res) => {
        this.basicInfo = { avatar: res.data.url ? res.data.url : '' };
        this.cdr.detectChanges();
      });
    }
    const userSub = this.store.pipe(select(currentUser)).subscribe((res) => {
      this.companyId = res !== undefined ? res.companyID : -1;
    });
    this.createForm();
    this.subscriptions.push(userSub);
    const successSubscription = this.store
      .pipe(select(selectContactLatestSuccessfullAction), skip(1))
      .subscribe((res) => {
        if (res && res.action_type) {
          switch (res.action_type) {
            case ContactActionTypes.ContactCreated:
              this.dialogRef.close({ added: true });
              break;
            default:
              break;
          }
        }
      });
    this.subscriptions.push(successSubscription);
    this.contactService.getLanguagePreference().subscribe((result) => {
      this.languages = result ? result.data : [];
      this.cdr.detectChanges();
    });
  }

  getTitle() {
    return this.translate.instant('CONTACT.NEW_CONTACT');
  }

  /**
   * On destroy
   */
  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  /**
   * Save data
   */
  onSubmit() {
    this.hasFormErrors = false;
    const controls = this.contactForm.controls;
    /** check form */
    if (this.contactForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      this.hasFormErrors = true;
      return;
    }
    this.clicked = true;
    this.createContact(this.prepareContact());
  }

  /**
   * Returns object for saving
   */
  prepareContact(): ContactModel {
    const controls = this.contactForm.controls;
    const _contact = new ContactModel();
    _contact.id = null;
    _contact.company_id = this.companyId;
    _contact.contact_id = '';
    _contact.nickname = controls.nickname.value;
    _contact.first_name = controls.first_name.value;
    _contact.middle_name = controls.middle_name.value;
    _contact.last_name = controls.last_name.value;
    _contact.last_name2 = controls.last_name2.value;
    _contact.entity_type = controls.entity_type.value;
    _contact.entity_id = controls.entity_id.value;
    _contact.email = controls.email.value;
    _contact.gender = controls.gender.value ? controls.gender.value : null;
    _contact.cell_phone =
      controls.cell_phone.value === '' ? null : controls.cell_phone.value.replace(/\D/g, '');
    _contact.phone2 =
      controls.phone2.value === '' ? null : controls.phone2.value.replace(/\D/g, '');
    _contact.home_phone =
      controls.home_phone.value !== null
        ? controls.home_phone.value.replace(/\D/g, '')
        : controls.home_phone.value;
    _contact.fax =
      controls.fax.value !== null ? controls.fax.value.replace(/\D/g, '') : controls.fax.value;
    _contact.state = controls.state.value ? controls.state.value.toUpperCase() : '';
    _contact.pin_number = controls.pin_number.value;
    _contact.city = controls.city.value;
    _contact.zip = controls.zip.value;
    _contact.address_1 = controls.address1.value;
    _contact.address_2 = controls.address2.value;
    _contact.note = controls.note.value;
    _contact.is_active = controls.isActive.value;
    _contact.tags = this.tags.value;
    _contact.urbanization = controls.urbanization.value;
    _contact.preferred_time = controls.preferred_time.value;
    _contact.dob = this.datePipe.transform(controls.dob.value, 'yyyy-MM-dd');
    _contact.language = controls.language_preference.value
      ? {
          id: controls.language_preference.value,
        }
      : null;
    return _contact;
  }

  /**
   * Create contact
   *
   * @param _contact: Contact
   */
  createContact(_contact: ContactModel) {
    this.store.dispatch(new ContactOnServerCreated({ contact: _contact }));
    this.store.pipe(select(selectContactsActionLoading)).subscribe((res) => {
      if (res !== true) {
        const errorStateSubscription = this.store
          .pipe(select(selectContactErrorState))
          .subscribe((error) => {
            if (error) {
              if (error && error.errors && error.errors[0]) {
                this.submissionErrorObj = [{ error: error.errors[0].details }];
              } else {
                this.submissionErrorObj = [{ error: 'GLOBAL.UNSPECIFIED_ERROR' }];
              }
              this.hasSubmissionErrors = true;
            }
          });
        this.subscriptions.push(errorStateSubscription);
      }
      this.cdr.detectChanges();
    });
  }

  isControlHasError(controlName: string, validationType: string): boolean {
    const control = this.contactForm.controls[controlName];
    if (!control) {
      return false;
    }
    return control.hasError(validationType) && (control.dirty || control.touched);
  }

  close() {
    this.dialogRef.close();
  }

  createForm() {
    this.contactForm = this.contactFB.group({
      urbanization: [null, Validators.maxLength(48)],
      nickname: [null],
      first_name: [null, Validators.required],
      last_name: [null, Validators.required],
      middle_name: [null],
      last_name2: [null],
      gender: [''],
      entity_type: [],
      entity_id: [],
      email: [null, Validators.email],
      cell_phone: ['', Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/)],
      home_phone: ['', Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/)],
      phone2: ['', Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/)],
      fax: ['', Validators.pattern(/^\(([0-9]{3})\)[ ]([0-9]{3})[-]([0-9]{4})$/)],
      state: [null],
      pin_number: [null, Validators.pattern('^[0-9]*$')],
      city: [null, Validators.compose([Validators.maxLength(30)])],
      zip: [null, Validators.compose([Validators.pattern(/(^\d{5}$)|(^\d{5}-\d{4}$)/)])],
      address1: [null, Validators.compose([Validators.maxLength(48)])],
      address2: [null, Validators.maxLength(48)],
      isActive: [true],
      note: [null, Validators.maxLength(10000)],
      dob: [],
      language_preference: [],
      preferred_time: [],
    });
    if (this.data) {
      for (const control of Object.values(this.contactForm.controls)) {
        control.disable();
      }
      this.contactForm.controls['pin_number'.toString()].setValue(this.data.contact.pin_number);
      this.contactForm.controls['first_name'.toString()].setValue(this.data.contact.first_name);
      this.contactForm.controls['nickname'.toString()].setValue(this.data.contact.nickname);
      this.contactForm.controls['middle_name'.toString()].setValue(this.data.contact.middle_name);
      this.contactForm.controls['last_name'.toString()].setValue(this.data.contact.last_name);
      this.contactForm.controls['last_name2'.toString()].setValue(this.data.contact.last_name2);
      this.contactForm.controls['preferred_time'.toString()].setValue(
        this.data.contact.preferred_time,
      );
      this.contactForm.controls['email'.toString()].setValue(this.data.contact.email);
      this.contactForm.controls['gender'.toString()].setValue(
        this.data.contact.gender ? this.data.contact.gender : '',
      );
      this.contactForm.controls['cell_phone'.toString()].setValue(this.data.contact.cell_phone);
      this.contactForm.controls['home_phone'.toString()].setValue(this.data.contact.home_phone);
      this.contactForm.controls['phone2'.toString()].setValue(this.data.contact.phone2);
      this.contactForm.controls['fax'.toString()].setValue(this.data.contact.fax);
      this.contactForm.controls['note'.toString()].setValue(this.data.contact.note);
      this.contactForm.controls['state'.toString()].setValue(this.data.contact.state);
      this.contactForm.controls['dob'.toString()].setValue(
        this.data.contact.dob === null ? '' : new Date(this.data.contact.dob),
      );
      this.contactForm.controls['isActive'.toString()].setValue(this.data.contact.is_active);
      this.urbanizationValue = this.data.contact?.location?.urbanization;
      this.Address1Value = this.data.contact?.location?.address1;
      this.Address2Value = this.data.contact?.location?.address2;
      this.cityValue = this.data.contact?.location?.city;
      this.zipValue = this.data.contact?.location?.zip;
      this.stats = this.data.contact?.location?.state;
      if (this.fromHis) {
        this.readOnlyTags = this.data.contact?.tags;
        this.tags$.next(this.data.contact?.tags);
        this.loadingTags = false;
      } else {
        this.loadTags();
      }
    }
  }

  getStatusString(): string {
    switch (this.contactForm.get('isActive').value) {
      case true:
        return this.translate.instant('GLOBAL.ACTIVE');
      case false:
        return this.translate.instant('GLOBAL.INACTIVE');
    }
    return '';
  }

  putDash(e) {
    let x = e;
    if (x.length === 6 && x.charAt(5) !== '-') {
      x = x.slice(0, 5) + '-' + x.charAt(x.length - 1);
    }
    return x;
  }
}
